(() => {
  class Tabs extends HTMLElement {
    /**
     * Initializes the Tabs component.
     */
    constructor() {
      super();

      this.ACTIVE_TAB_CLASS = "tabs__tab--active";
      this.ACTIVE_CONTENT_CLASS = "tab-content--active";
      this.activeTab = null;
      this.activeContent = null;
      this.activeContentIndex = 0;
    }

    /**
     * Lifecycle method called when the element is added to the DOM.
     */
    connectedCallback() {
      this.setActiveElements();
      this.addListeners();
      this.setPositions();
    }

    /**
     * Sets the initial active tab and content elements.
     */
    setActiveElements() {
      this.activeTab = this.tabs[0] || null;
      this.activeContent = this.tabContents[0] || null;
    }

    /**
     * Adds click event listeners to tabs and subscribes to optionSelected events.
     */
    addListeners() {
      this.tabs.forEach(tab => tab.addEventListener("click", () => this.toggleTab(tab)));

      subscribe(PUB_SUB_EVENTS.optionSelected, data => this.openTab(data));
    }

    /**
     * Opens a tab based on the provided target data.
     * @param {Object} target - The target tab data.
     */
    openTab(target) {
      if (target.id !== this.id) return;

      const tab = Array.from(this.tabs).find(tab => tab.dataset.tab === target.index);

      if (tab) this.toggleTab(tab);
    }

    /**
     * Sets the position of each tab content.
     */
    setPositions() {
      this.tabContents.forEach(content => this.updateTabContentPosition(content));
    }

    /**
     * Activates the selected tab and updates tab contents.
     * @param {Element} tab - The tab element to activate.
     */
    toggleTab(tab) {
      tab.classList.add(this.ACTIVE_TAB_CLASS);

      if (tab !== this.activeTab) {
        this.updateTabs(tab);
        this.updateTabContents();

        publish(PUB_SUB_EVENTS.tabChange, {
          tab: this.activeTab.dataset.tab,
          wrapper: this.tabWrapper
        });
      }
    }

    /**
     * Updates the active tab and content references.
     * @param {Element} tab - The tab element to set as active.
     */
    updateTabs(tab) {
      this.activeContent.classList.remove(this.ACTIVE_CONTENT_CLASS);
      this.activeTab.classList.remove(this.ACTIVE_TAB_CLASS);
      this.activeTab.tabIndex = -1;
      this.activeContent.tabIndex = -1;
      this.activeTab.ariaSelected = "false";
      this.activeTab = tab;
      this.activeTab.tabIndex = 0;
      this.activeTab.ariaSelected = "true";
      this.activeContentIndex = tab.dataset.tab;
    }

    /**
     * Updates the visibility and position of tab contents.
     */
    updateTabContents() {
      const tabIndex = this.activeTab.dataset.tab;

      this.tabContents.forEach(content => {
        const contentIndex = content.dataset.tabContent;

        if (contentIndex === tabIndex) this.updateTabContent(content);

        this.updateTabContentPosition(content);
      });
    }

    /**
     * Sets the active tab content.
     * @param {Element} content - The content element to activate.
     */
    updateTabContent(content) {
      this.activeContent = content;
      this.activeContent.tabIndex = 0;
      this.activeContent.classList.add(this.ACTIVE_CONTENT_CLASS);
    }

    /**
     * Updates the position of a tab content element.
     * @param {Element} content - The content element to position.
     */
    updateTabContentPosition(content) {
      content.style.left = "-" + 100 * this.activeContentIndex + "%";
    }

    /**
     * Returns the tabs component ID.
     */
    get id() {
      return this.hasAttribute("data-tabs-id") ? this.dataset.tabsId : "";
    }

    /**
     * Returns the identifier selector for tabs.
     */
    get identifier() {
      return this.id ? `[data-tabs-id="${this.id}"]` : "";
    }

    /**
     * Returns all tab elements.
     */
    get tabs() {
      return this.querySelectorAll(`[data-tab]${this.identifier}`);
    }

    /**
     * Returns the closest tab wrapper element.
     */
    get tabWrapper() {
      return this.closest("[data-tabs-wrapper]") ?? this;
    }

    /**
     * Returns all tab content elements.
     */
    get tabContents() {
      return this.tabWrapper.querySelectorAll(`[data-tab-content]${this.identifier}`);
    }
  }

  if (!customElements.get("tabs-wrapper")) {
    customElements.define("tabs-wrapper", Tabs);
  }
})();